import type { SessionLite } from "@classes-service-types/session-lite";
import { addDays, format, isValid, parse, startOfDay } from "date-fns";

const DATE_ONLY_FORMAT = "yyyy-MM-dd";

const utcMidnightUnixToLocalMidnight = (ms: number): Date => {
  // Interpret the instant, then extract the UTC calendar date.
  const utc = new Date(ms);
  const y = utc.getUTCFullYear();
  const m = utc.getUTCMonth(); // 0-based
  const d = utc.getUTCDate();
  // Construct local midnight for that same calendar date.
  return new Date(y, m, d, 0, 0, 0, 0);
};

const parseInputToDate = (input: string): Date => {
  const trimmed = input.trim();

  // Unix timestamp (seconds or milliseconds)
  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length === 10) {
      const seconds = Number(trimmed);
      const d = utcMidnightUnixToLocalMidnight(seconds * 1000);
      if (!Number.isFinite(seconds) || !isValid(d)) {
        throw new Error(`Invalid unix seconds timestamp: "${input}"`);
      }
      return d;
    }

    if (trimmed.length === 13) {
      const millis = Number(trimmed);
      const d = utcMidnightUnixToLocalMidnight(millis);
      if (!Number.isFinite(millis) || !isValid(d)) {
        throw new Error(`Invalid unix milliseconds timestamp: "${input}"`);
      }
      return d;
    }

    throw new Error(`Unix timestamp must be 10 (seconds) or 13 (milliseconds) digits: "${input}"`);
  }

  // yyyy-MM-dd date-only
  const parsed = parse(trimmed, DATE_ONLY_FORMAT, new Date());
  if (!isValid(parsed)) {
    throw new Error(`Invalid date: "${input}"`);
  }
  if (format(parsed, DATE_ONLY_FORMAT) !== trimmed) {
    throw new Error(`Date must be in yyyy-MM-dd format: "${input}"`);
  }
  return parsed;
};

/**
 * Returns midnight (start of day) of the following day in the user's local timezone.
 *
 * - If input is yyyy-MM-dd => treats it as local calendar date.
 * - If input is unix timestamp => uses the local calendar day of that instant.
 */
export const endOfLocalDay = (input: string): Date => {
  const baseDate = parseInputToDate(input);
  return startOfDay(addDays(baseDate, 1));
};

export const isPastDate = (checkDate: Date): boolean => {
  return new Date() > checkDate;
};

export type PastSessionDeadlineInput = SessionLite | Record<string, unknown>;

export const pastSessionDeadline = (session: PastSessionDeadlineInput | undefined | null): boolean => {
  const hasApplicationDeadline = session?.application_deadline;
  if (!session || !hasApplicationDeadline) {
    return false;
  }
  const applicationDeadline = String(session.application_deadline);
  return isPastDate(endOfLocalDay(applicationDeadline));
};

/** Same session start fields as `SessionLite` (travel arrival vs academic start). */
export type PastSessionStartDateInput = Pick<SessionLite, "session_travel_details" | "academic_start_date"> &
  Record<string, unknown>;

export const pastSessionStartDate = (session: PastSessionStartDateInput): boolean => {
  const sessionArrivalDate = session.session_travel_details?.[0]?.arrival_date;
  const academicStartDate = session.academic_start_date;
  const sessionStartDate = sessionArrivalDate ?? academicStartDate;
  return isPastDate(endOfLocalDay(String(sessionStartDate)));
};
